import {Container} from '../../components/Container';
import React, {memo, useCallback, useMemo, useRef, useState, useEffect} from 'react';
import {useLocalization} from '../../hooks/localization';
import {Row} from '../../components/Row';
import {Column} from '../../components/Column';
import {Widget} from '../../components/Widget';
import {Button} from '../../components/Button';
import intercomIcon from '../../assets/images/icons/intercom.png';
import {useRPC} from '../../hooks/rpc';
import './index.scss';
import {useKeyboard} from '../../hooks/keyboard';
import {onClickHandler} from '../../utils/onclick';
import {useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useSet} from '../../hooks/set';
import {useMessage} from '../../hooks/message';
import leftArrowIcon from '../../assets/images/icons/media/left.png';
import {backButtonToggle, groupSettingAction} from "../../store/ui/actions";
import messageIcon from '../../assets/images/icons/message.png';

type ConciergeItemType = {
    id: string;
    name: string;
}

export const ConciergeMessageScreen = memo(
    function ConciergeMessageScreen({selected, back}: { selected: ConciergeItemType, back: () => void }) {
        const {t} = useLocalization();
        const {success, failure} = useMessage();
        const [rpc] = useRPC();
        const [setActiveInput, renderKeyboard] = useKeyboard();
        const inputRef = useRef<HTMLInputElement>(null);
        const [message, setMessage] = useState('');
        const dispatch = useDispatch();
        dispatch(backButtonToggle({is_open: true, to: '/'}));
        dispatch(groupSettingAction({visible: false}));
        const conciergeRequest = useCallback(() => {
            rpc.createConciergeRequest(selected.id, message).then(() => {
                success(t('concierge_request_success'));
            }).catch(failure);
            back();
        }, [rpc, selected?.id, message, failure, back, success, t]);

        const onFocus = useCallback(() => {
            setActiveInput([inputRef, message, setMessage]);
        }, [message, setActiveInput]);

        const onBlur = useCallback(() => {
            setActiveInput(null);
        }, [setActiveInput]);

        return (
            <>
                <div className="concierge-message-container">
                    <div className="name">
                        <img src={leftArrowIcon} className="back-button" onClick={back} />
                        <span>{selected.name}</span>
                    </div>
                    <div className="input-container">
                        <input
                            maxLength={250}
                            placeholder={t('type_a_message')}
                            onChange={e => setMessage(e.target.value)}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            value={message}
                            ref={inputRef}
                        />
                        <button {...onClickHandler(conciergeRequest)}>
                            <span>{t('send')}</span>
                        </button>
                    </div>
                </div>
                {
                    renderKeyboard()
                }
            </>
        );
    },
);

export const ConciergeScreen = memo(function ConciergeScreen() {
    const {t} = useLocalization();
    const history = useHistory();
    const dispatch = useDispatch();
    const conciergeList = useSelector(state => state.control.conciergeList);

    const filteredConciergeList = useMemo(() => {
        return conciergeList?.filter(item => item.info?.url) || [];
    }, [conciergeList]);

    const conciergeTypeSet = useSet(useSelector(state => state.control.conciergeTypeList));

    const [selected, setSelected] = useState<ConciergeItemType>(null);

    const itemClicked = useCallback((item: ConciergeItemType) => {
        setSelected(item);
    }, []);

    const goToMeetingPage = useCallback(() => {
        history.push('/meeting');
    }, [history]);

    const goToMessagingPage = useCallback(() => {
        history.push("/messaging")
    }, [history])

    useEffect(() => {
      dispatch(backButtonToggle({ is_open: true, to: "/" }));
    }, [dispatch]);

    return (
        <Container className="full-screen concierge-screen">
            <div className="page-wrapper">
                {
                    renderIf(!selected)(() => (
                        <Row style={{padding: '8px 7px'}}>
                            <Column min={3} max={6}>
                                <Widget style={{padding: 0}}>
                                    <Button icon={intercomIcon}
                                            title={t('in_site_call')}
                                            onClick={goToMeetingPage} disablePrefix={true}>
                                    </Button>
                                </Widget>
                            </Column>
                            {
                                filteredConciergeList.map(item => item?.info?.key && (
                                    <Column min={3} max={6} key={`concierge_${item.id}`}>
                                        <Widget style={{padding: 0}}>
                                            <Button icon={messageIcon}
                                                    title={item.name}
                                                    onClick={goToMessagingPage}>
                                            </Button>
                                        </Widget>
                                    </Column>
                                ))
                            }
                        </Row>
                    ), () => (
                        <ConciergeMessageScreen selected={selected} back={() => setSelected(null)}/>
                    ))
                }
            </div>
        </Container>
    );
});
